import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { HelpCircle, ArrowRight, BadgeCheck } from "lucide-react";
import { api } from "@/lib/api";
import { FOKUSTYP_META, FOKUSTYP_DISPLAY_ORDER } from "@/components/FokustypGlyphs";

const useAvatarBlob = (url) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!url) return undefined;
    let alive = true;
    let obj = null;
    api.get(url.replace(/^\/api\//, ""), { responseType: "blob" })
      .then((r) => { if (alive) { obj = URL.createObjectURL(r.data); setSrc(obj); } })
      .catch(() => {});
    return () => { alive = false; if (obj) URL.revokeObjectURL(obj); };
  }, [url]);
  return src;
};

const TypIcon = ({ typ, size }) => (
  <img src={FOKUSTYP_META[typ].iconSrc} alt={FOKUSTYP_META[typ].name} width={size} height={size} loading="eager" />
);

const Medaillon = ({ typ, photo, testId }) => {
  const Icon = FOKUSTYP_META[typ] ? (p) => <TypIcon typ={typ} {...p} /> : null;
  return (
    <div data-testid={testId} className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white dark:bg-slate-800 border-2 border-[#4563BB]/30 dark:border-[#7A92D4]/50 flex items-center justify-center overflow-hidden">
      {photo ? <img src={photo} alt="" className="w-full h-full object-cover" />
        : Icon ? <Icon size={44} /> : <HelpCircle className="w-9 h-9 text-slate-400" />}
      {photo && Icon && (
        <span className="absolute -bottom-0.5 -right-0.5 w-7 h-7 rounded-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 flex items-center justify-center"><Icon size={20} /></span>
      )}
    </div>
  );
};

export const MiniBalken = ({ werte, highlight, testId }) => (
  <div data-testid={testId} className="mt-3 w-full space-y-1">
    {FOKUSTYP_DISPLAY_ORDER.map((t) => (
      <div key={t} className="flex items-center gap-1.5 text-[10px] text-slate-500 dark:text-slate-400">
        <span className="w-9 text-left">{FOKUSTYP_META[t].name}</span>
        <span className="flex-1 h-1.5 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
          <span className={`block h-full rounded-full ${t === highlight ? "bg-[#F3651D]" : "bg-[#4563BB]/60 dark:bg-[#7A92D4]/70"}`} style={{ width: `${werte[t] || 0}%` }} />
        </span>
        <span className="w-7 text-right tabular-nums" data-testid={`${testId}-${t}`}>{werte[t] || 0}%</span>
      </div>
    ))}
  </div>
);

const CtaLink = ({ to, children, testId }) => (
  <Link to={to} data-testid={testId} className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-[#F3651D] hover:underline">
    {children} <ArrowRight className="w-3 h-3" />
  </Link>
);

export const IchSeite = ({ ich, isPro }) => {
  const photo = useAvatarBlob(ich.avatar_url);
  const typ = ich.fokustyp;
  return (
    <div data-testid="begegnung-ich" data-quelle={ich.quelle} className="flex flex-col items-center text-center min-w-0">
      <Medaillon typ={typ} photo={photo} testId="begegnung-ich-medaillon" />
      <p className="mt-2 text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">Du{ich.first_name ? ` · ${ich.first_name}` : ""}</p>
      <p data-testid="begegnung-ich-typ" className="text-base font-extrabold text-slate-900 dark:text-white">{typ ? FOKUSTYP_META[typ].name : "Noch offen"}</p>
      {isPro && ich.quelle === "voll" && ich.secondary_fokustyp && (
        <p className="text-xs text-slate-500 dark:text-slate-400">mit {FOKUSTYP_META[ich.secondary_fokustyp].name}-Anteil</p>
      )}
      {isPro && ich.quelle === "voll" && (
        <span data-testid="begegnung-ich-voll" className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 dark:text-emerald-300"><BadgeCheck className="w-3 h-3" /> Vollständiges Profil</span>
      )}
      {isPro && ich.percent_by_type && <MiniBalken werte={ich.percent_by_type} highlight={typ} testId="begegnung-ich-balken" />}
      {ich.quelle === "keiner" && !isPro && <CtaLink to="/dashboard/test" testId="begegnung-ich-quickcheck-cta">QuickCheck machen (2 Min.)</CtaLink>}
      {ich.quelle === "keiner" && isPro && <CtaLink to="/dashboard/pro-analyse" testId="begegnung-ich-test-cta">20-Fragen-Test machen</CtaLink>}
      {ich.quelle === "quickcheck" && isPro && <CtaLink to="/dashboard/pro-analyse" testId="begegnung-ich-schaerfen-cta">Profil schärfen: 20-Fragen-Test</CtaLink>}
    </div>
  );
};

export const GegenueberSeite = ({ gegenueber, isPro }) => {
  const typ = gegenueber.fokustyp;
  const pct = gegenueber.verteilung;
  return (
    <div data-testid="begegnung-gegenueber" className="flex flex-col items-center text-center min-w-0">
      <Medaillon typ={typ} testId="begegnung-gegenueber-medaillon" />
      <p className="mt-2 text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">Dein Gegenüber</p>
      <p data-testid="begegnung-gegenueber-typ" className="text-base font-extrabold text-slate-900 dark:text-white">
        {typ ? FOKUSTYP_META[typ].name : "Noch unklar"}
        {isPro && pct && typ && <span data-testid="begegnung-gegenueber-prozent" className="ml-1 text-sm font-bold text-[#F3651D]">{pct[typ]} %</span>}
      </p>
      {isPro && pct && <MiniBalken werte={pct} highlight={typ} testId="begegnung-gegenueber-balken" />}
    </div>
  );
};
